import classNames from 'clsx';
import React, { useCallback, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { IS_MOBILE_BROWSER, IS_MY_TEAM, IS_ONPREMISE, IS_TARM } from 'reactApp/appHelpers/configHelpers';
import { publishHelper } from 'reactApp/appHelpers/publishHelper';
import { getEditorItem, getEditorState } from 'reactApp/modules/editor/editor.selectors.core';
import type { EditorItem } from 'reactApp/modules/editor/editor.types';
import { isOnPremR7Product } from 'reactApp/modules/features/features.helpers';
import styles from 'reactApp/ui/EditorHeader/BizInlineEditorToolbar/BizInlineEditorToolbar.css';
import { EditorTools } from 'reactApp/ui/EditorHeader/BizInlineEditorToolbar/components/EditorTools';
import { OfflineModeButton } from 'reactApp/ui/EditorHeader/BizInlineEditorToolbar/components/OfflineModeButton/OfflineModeButton';
import { updateWeblinkInUrlAction } from 'reactApp/ui/EditorHeader/WhiteEditorHeader/EditorHeader';
import { createGaSender, sendGa } from 'reactApp/utils/ga';
import { EditorMode } from 'server/helpers/editors/types';

type InlineEditorToolbarProps = {
    file: EditorItem | null;
    isPublic: boolean;
    fromViewer?: boolean;
    mode: EditorMode;
    updateWeblinkInUrl: (id?: string, weblink?: string) => void;
    backLink: string;
    gaCategory: string;
    isReady: boolean;
};

const InlineEditorToolbar: React.FC<InlineEditorToolbarProps> = ({
    file,
    gaCategory,
    isPublic,
    mode,
    fromViewer,
    isReady,
    updateWeblinkInUrl,
    backLink,
}) => {
    const gaSender = useMemo(() => createGaSender(`${gaCategory}-${isPublic ? 'public' : 'home'}`), [gaCategory, isPublic]);

    const handlePublish = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();

            gaSender('publish', 'click', { type: 'button' });

            publishHelper({ item: file, gaSuffix: '_header', fromEditor: true });
        },
        [file, gaSender]
    );

    const handleBack = useCallback(() => {
        window.location.href = backLink;
        window.opener = null;
    }, [backLink]);

    useEffect(() => {
        gaSender('show');

        if (isPublic) {
            sendGa('public-edit', 'file-type', 'document');
            const ext = file?.ext;
            if (ext) {
                sendGa('public-edit', 'file-extention', ext);
            }
        } else {
            updateWeblinkInUrl(file?.id, file?.weblink);
        }
    }, []);

    useEffect(() => {
        updateWeblinkInUrl(file?.id, file?.weblink);
    }, [file?.id, file?.weblink, updateWeblinkInUrl]);

    if (IS_MOBILE_BROWSER && IS_ONPREMISE && isOnPremR7Product) {
        return (
            <div className={styles.root}>
                <div
                    className={classNames(styles.logo, styles.logoFullWidth, {
                        [styles.logo__tarm]: IS_TARM,
                        [styles.logo__workdisk]: !IS_TARM,
                    })}
                    data-ext={String(file?.ext).toLowerCase()}
                />
            </div>
        );
    }

    return (
        <div className={styles.root}>
            <div className={classNames({ [styles.leftGroup]: true, [styles.leftGroupViewer]: fromViewer })}>
                {(IS_ONPREMISE || IS_MY_TEAM) && (
                    <div
                        className={classNames({
                            [styles.logo]: true,
                            [styles.logoViewer]: fromViewer,
                            [styles.logo__tarm]: IS_TARM,
                            [styles.logo__workdisk]: !IS_TARM,
                        })}
                        onClick={handleBack}
                        data-ext={String(file?.ext).toLowerCase()}
                    />
                )}
                {mode === EditorMode.EDIT && <OfflineModeButton isReady={isReady} />}
            </div>
            <div
                className={classNames({
                    [styles.rightGroup]: true,
                    [styles.modeView]: mode === EditorMode.VIEW,
                    [styles.modeEdit]: mode === EditorMode.EDIT,
                    [styles.modeComment]: mode === EditorMode.COMMENT,
                })}
            >
                <EditorTools file={file} isReady={isReady} isPublic={isPublic} handlePublish={handlePublish} />
            </div>
        </div>
    );
};

InlineEditorToolbar.displayName = 'BizInlineEditorToolbar';

const mapStateToProps = (state, props) => {
    const { isPublic, itemSelector = getEditorItem } = props;

    const { isReady } = getEditorState(state);
    const file = itemSelector(state);

    return {
        backLink: isPublic ? '/home' : `/home${file?.parent}`,
        updateWeblinkInUrl: updateWeblinkInUrlAction,
        file,
        isReady,
    };
};

export const BizInlineEditorToolbar = connect(mapStateToProps)(InlineEditorToolbar);
